import "./AdsCreativePreviewCard.scss";
import { ReactComponent as IconMonitor } from "assets/images/IconMonitor.svg";
import { ReactComponent as IconMobile } from "assets/images/IconMobile.svg";
import { ReactComponent as IconSettings } from "assets/images/IconSettings.svg";
import { ReactComponent as IconDelete } from "assets/images/IconDelete.svg";
import { ReactComponent as IconLike } from "assets/images/IconLike.svg";
import { ReactComponent as IconComment } from "assets/images/IconComment.svg";
import { ReactComponent as IconShare } from "assets/images/IconShare.svg";
import { ReactComponent as IconGlobe } from "assets/images/IconGlobe.svg";
import { ReactComponent as InstaLikeIcon } from "assets/images/InstaLikeIcon.svg";
import { ReactComponent as InstaCommentIcon } from "assets/images/InstaCommentIcon.svg";
import { ReactComponent as InstaShareIcon } from "assets/images/InstaShareIcon.svg";
import { ReactComponent as InstaSaveIcon } from "assets/images/InstaSaveIcon.svg";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  AddCircleOutline,
  BookmarkBorder,
  ChevronRight,
  Close,
  FacebookOutlined,
  FavoriteBorder,
  InfoOutlined,
  Instagram,
  MoreHoriz,
  Send,
} from "@mui/icons-material";
import CustomFormControl from "features/bidding-center/Dropdown/CustomFormControl";
import CustomOutlinedInput from "components/CustomOutlinedInput/CustomOutlinedInput";
import { useEffect, useRef, useState } from "react";
import {
  META_TRAVEL_ADS_LANGUAGES,
  primaryTextDropdownOptions,
} from "features/meta-travel-ads-campaign/MetaTravelAdsFormComponents/AudiencesForm/utils";
import META_TRAVEL_ADS_LANGUAGE_CRITERION from "features/meta-travel-ads-campaign/constants/meta-travel-ads-criterion/metaTravelAdsLanguageCriterion";
import MetaMediaLibrary from "../MetaMediaLibrary/MetaMediaLibrary";
import {
  validateCallToAction,
  validateDeepLink,
  validateFeedLinkDescription,
  validateHeadline,
  validatePrimaryText,
  validateWebSiteURL,
} from "features/meta-travel-ads-campaign/Utils/validators/adsCreativeValidators";
import FallbackImage from "assets/images/FallbackImage.png";
import callToActionOptions from "features/meta-travel-ads-campaign/MetaTravelAdsFormComponents/AdsCreative/utils/callToActionOptions";
import { getHotelCatalogData } from "features/meta-travel-ads-campaign/Utils/getHotelCatalogData";

const tooltipStyles = {
  display: "flex",
  alignItems: "center",
  maxWidth: 156,
  color: "#BAB7B7",
  borderRadius: "10px",
  background: "#FFF",
  boxShadow: "0 5px 8px 0 rgba(0, 0, 0, 0.25)",
  fontFamily: "Roboto",
  fontSize: 10,
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "normal",
  padding: "8px 12px",
};

const menuBarStyles = {
  display: "flex",
  alignItems: "center",
  gap: "24px"
};

const TooltipContent = ({ text }) => (
  <div style={{ display: "flex", alignItems: "start", gap: 8 }}>
    <InfoOutlined sx={{ color: "black", width: "12px", height: "12px" }} />{" "}
    <span>{text}</span>
  </div>
);

const AdsCreativePreviewCard = ({
  id,
  onDelete,
  hotelId,
  adsCreativeCards,
  setAdsCreativeCards,
  selectedLanguage = [],
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const editableCard = adsCreativeCards.find((card) => card?.ad_card_id === id);
  const language = editableCard?.ad_language_id;
  const primaryText = editableCard?.primary_text?.assetContent || "";
  const websiteURL = editableCard?.web_site_url?.url || "";
  const headline = editableCard?.headline?.assetContent || "";
  const callToAction = editableCard?.call_to_action?.assetContent || "";
  const feedLinkDescription =
    editableCard?.feed_link_description?.assetContent || "";
  const deepLinkToWebsite = editableCard?.deep_link_to_website?.url || "";
  const isMultiAds = editableCard?.multi_advertiser_ads || false;

  const [openMediaLibrary, setOpenMediaLibrary] = useState(false);
  const [imageSource, setImageSource] = useState(
    editableCard?.marketing_image_source === "MEDIA_LIBRARY" ? "media" : "catalogue"
  );
  const [catalogueImageUrl, setCatalogueImageUrl] = useState(null);
  const mediaLibraryImageRef = useRef(
    editableCard?.marketing_image_source === "MEDIA_LIBRARY" ? (editableCard?.marketing_image?.url || null) : null
  );
  const [headlineStatus, setHeadlineStatus] = useState(null);
  const [primaryTextStatus, setPrimaryTextStatus] = useState(null);
  const [websiteURLStatus, setWebsiteURLStatus] = useState(null);
  const [callToActionStatus, setCallToActionStatus] = useState(null);
  const [feedLinkDescriptionStatus, setFeedLinkDescriptionStatus] =
    useState(null);
  const [deepLinkToWebsiteStatus, setDeepLinkToWebsiteStatus] = useState(null);
  const [preview, setPreview] = useState("facebook");

  const isFacebook = preview === "facebook";

  const [anchorEl, setAnchorEl] = useState(null);
  const openPrimarytextMenu = Boolean(anchorEl);

  const [headlineAnchorEl, setHeadlineAnchorEl] = useState(null);
  const openHeadlineMenu = Boolean(headlineAnchorEl);

  const [descriptionAnchorEl, setDescriptionAnchorEl] = useState(null);
  const openDescriptionMenu = Boolean(descriptionAnchorEl);

  const setMarketingImageOnCard = (url) =>
    setAdsCreativeCards((prevCards) =>
      prevCards.map((card) =>
        card?.ad_card_id === id
          ? { ...card, marketing_image: { url: url || null } }
          : card,
      ),
    );

  // Fetch catalogue image once per hotel and apply it whenever
  // the user has the "Use Catalogue image" option selected.
  useEffect(() => {
    let isMounted = true;

    const applyCatalogueImage = async () => {
      if (!hotelId) return;

      let url = catalogueImageUrl;
      if (!url) {
        const result = await getHotelCatalogData(hotelId);
        if (!isMounted) return;
        if (result.success && result.imageUrl) {
          url = result.imageUrl;
          setCatalogueImageUrl(url);
        }
      }

      if (imageSource === "catalogue" && url) {
        setMarketingImageOnCard(url);
      }
    };

    if (imageSource === "catalogue") {
      applyCatalogueImage();
    } else if (imageSource === "media") {
      // Restore previously selected media library image (if any).
      setMarketingImageOnCard(mediaLibraryImageRef.current);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSource, hotelId]);

  // Apply catalogue defaults (language, website URL, description, call to action)
  // when the card is newly created (no existing data filled in).
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (!hotelId) return;
    // Wait until selectedLanguage is available before applying defaults
    if (!selectedLanguage?.length) return;

    let isMounted = true;
    const applyDefaults = async () => {
      const result = await getHotelCatalogData(hotelId);
      if (!isMounted) return;

      const defaultLanguageId = (() => {
        if (!selectedLanguage?.length) return undefined;
        const campaignLang = selectedLanguage[0];
        // Campaign Settings uses META_TRAVEL_ADS_LANGUAGE_CRITERION (different IDs).
        // Match by language_code to find the correct id in META_TRAVEL_ADS_LANGUAGES.
        let langCode = campaignLang.language_code;

        // In editor mode, selectedLanguage may not have language_code (loaded from API).
        // Look it up from the criterion list using id_lang.
        if (!langCode && campaignLang.id_lang) {
          const criterionEntry = META_TRAVEL_ADS_LANGUAGE_CRITERION.find(
            (c) => String(c.id_lang) === String(campaignLang.id_lang)
          );
          if (criterionEntry) langCode = criterionEntry.language_code;
        }

        if (langCode) {
          const match = META_TRAVEL_ADS_LANGUAGES.find(
            (l) => l.language_code === langCode || langCode.startsWith(l.language_code)
          );
          if (match) return match.id_lang;
        }
        return undefined;
      })();
      const defaultWebsiteUrl = result.success ? result.websiteUrl : undefined;
      const defaultDescription = result.success ? (result.description || result.hotelName) : undefined;

      setAdsCreativeCards((prevCards) => {
        const card = prevCards.find((c) => c?.ad_card_id === id);
        // Skip if card already has data (e.g. loaded from existing campaign)
        if (card?.ad_language_id || card?.web_site_url?.url || card?.call_to_action?.assetContent) {
          return prevCards;
        }
        return prevCards.map((c) => {
          if (c?.ad_card_id !== id) return c;
          const updated = { ...c };
          if (!updated.ad_language_id && defaultLanguageId) {
            updated.ad_language_id = defaultLanguageId;
          }
          if (!updated.web_site_url?.url && defaultWebsiteUrl) {
            updated.web_site_url = { url: defaultWebsiteUrl };
          }
          if (!updated.feed_link_description?.assetContent && defaultDescription) {
            updated.feed_link_description = { assetContent: defaultDescription };
          }
          if (!updated.call_to_action?.assetContent) {
            updated.call_to_action = { assetContent: "BOOK_TRAVEL" };
          }
          return updated;
        });
      });
      defaultsAppliedRef.current = true;
    };

    applyDefaults();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, id, selectedLanguage]);

  // Handlers grouped for clarity
  const handleChange = {
    language: (e) =>
      setAdsCreativeCards(
        adsCreativeCards.map((card) =>
          card?.ad_card_id === id
            ? { ...card, ad_language_id: e.target.value }
            : card,
        ),
      ),
    primaryText: (e) =>
      setAdsCreativeCards(
        adsCreativeCards.map((card) =>
          card?.ad_card_id === id
            ? { ...card, primary_text: { assetContent: e.target.value } }
            : card,
        ),
      ),
    websiteURL: (e) =>
      setAdsCreativeCards(
        adsCreativeCards.map((card) =>
          card?.ad_card_id === id
            ? { ...card, web_site_url: { url: e.target.value } }
            : card,
        ),
      ),
    headline: (e) =>
      setAdsCreativeCards(
        adsCreativeCards.map((card) =>
          card?.ad_card_id === id
            ? { ...card, headline: { assetContent: e.target.value } }
            : card,
        ),
      ),
    callToAction: (e) =>
      setAdsCreativeCards(
        adsCreativeCards.map((card) =>
          card?.ad_card_id === id
            ? {
                ...card,
                call_to_action: { assetContent: e.target.value },
                ...(e.target.value === "BOOK_TRAVEL" && {
                  deep_link_to_website: { url: "" },
                }),
              }
            : card,
        ),
      ),
    feedLinkDescription: (e) =>
      setAdsCreativeCards(
        adsCreativeCards.map((card) =>
          card?.ad_card_id === id
            ? {
                ...card,
                feed_link_description: { assetContent: e.target.value },
              }
            : card,
        ),
      ),
    deepLinkToWebsite: (e) =>
      setAdsCreativeCards(
        adsCreativeCards.map((card) =>
          card?.ad_card_id === id
            ? { ...card, deep_link_to_website: { url: e.target.value } }
            : card,
        ),
      ),
    multiAds: (e) =>
      setAdsCreativeCards(
        adsCreativeCards.map((card) =>
          card?.ad_card_id === id
            ? { ...card, multi_advertiser_ads: e.target.checked }
            : card,
        ),
      ),
  };

  const handleHeadlineBlur = async () => {
    const result = await validateHeadline(headline);
    setHeadlineStatus(
      result.success
        ? { success: true, error: null }
        : { success: false, error: result.error || "Invalid headline" },
    );
  };

  const handlePrimaryTextBlur = async () => {
    const result = await validatePrimaryText(primaryText);
    setPrimaryTextStatus(
      result.success
        ? { success: true, error: null }
        : { success: false, error: result.error || "Invalid primary text" },
    );
  };

  const handleWebsiteURLBlur = async () => {
    const result = await validateWebSiteURL(websiteURL);
    setWebsiteURLStatus(
      result.success
        ? { success: true, error: null }
        : { success: false, error: result.error || "Invalid website URL" },
    );
  };

  const handleCallToActionBlur = async () => {
    const result = await validateCallToAction(callToAction);
    setCallToActionStatus(
      result.success
        ? { success: true, error: null }
        : { success: false, error: result.error || "Invalid call to action" },
    );
  };

  const handleFeedLinkDescriptionBlur = async () => {
    const result = await validateFeedLinkDescription(feedLinkDescription);
    setFeedLinkDescriptionStatus(
      result.success
        ? { success: true, error: null }
        : {
            success: false,
            error: result.error || "Invalid description",
          },
    );
  };

  const handleDeepLinkBlur = async () => {
    const result = await validateDeepLink(deepLinkToWebsite);
    setDeepLinkToWebsiteStatus(
      result.success
        ? { success: true, error: null }
        : { success: false, error: result.error || "Invalid deep link" },
    );
  };

  return (
    <>
      <div className="main-container">
        <div className="ads-creative-preview-card">
          <div className="menu-bar">
            <span
              style={{ display: "flex", alignItems: "center", gap: "24px" }}
            >
              <span>
                <IconMonitor height={16} width={16} />
                <IconMobile height={12} width={12} />
              </span>
              <span>
                <FacebookOutlined
                  sx={{
                    color: isFacebook ? "black" : "lightgray",
                    cursor: "pointer",
                    height: "18px",
                    width: "18px",
                  }}
                  onClick={() => setPreview("facebook")}
                />
                <Instagram
                  sx={{
                    color: isFacebook ? "lightgray" : "black",
                    cursor: "pointer",
                    height: "18px",
                    width: "18px",
                  }}
                  onClick={() => setPreview("instagram")}
                />
              </span>
            </span>
            <span className="card-title">
              {(
                META_TRAVEL_ADS_LANGUAGES.find(
                  (lang) => String(lang.id_lang) === String(language),
                )?.language_name || ""
              ).toUpperCase()}
            </span>
            <span className="icons">
              <Tooltip title="Delete" placement="top">
                <IconDelete onClick={onDelete} />
              </Tooltip>
              <Tooltip title="Settings" placement="top">
                <IconSettings onClick={() => setShowSettings(true)} />
              </Tooltip>
            </span>
          </div>
          {isFacebook ? (
            <div className="title-bar">
              <div>
                <div className="ad-headline">Your Hotel Name</div>
                <div className="sponsored-text">
                  <span>
                    <IconGlobe /> Sponsored
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="title-bar">
              <div>
                <div className="ad-headline">Your Hotel Name</div>
                <div className="sponsored-text">
                  <span>Sponsored</span>
                </div>
              </div>
              <MoreHoriz />
            </div>
          )}
          {isFacebook && (
            <span className="primary-text">
              {primaryText || "Primary Text"}
            </span>
          )}
          <img
            className="ad-image"
            src={editableCard?.marketing_image?.url || FallbackImage}
            alt="Ad Preview"
          />
          {isFacebook ? (
            <div className="book-button">
              <Button variant="contained">
                {callToActionOptions.find(
                  (option) => option.id === callToAction,
                )?.name || "Book Now"}
              </Button>
            </div>
          ) : (
            <div className="insta-call-action">
              <span>
                {callToActionOptions.find(
                  (option) => option.id === callToAction,
                )?.name || "Book Now"}
              </span>{" "}
              <ChevronRight />
            </div>
          )}
          {!isFacebook && (
            <div className="insta-reactions">
              <span>
                <InstaLikeIcon />
                <InstaCommentIcon />
                <InstaShareIcon />
              </span>
              <InstaSaveIcon />
            </div>
          )}
          <div className="ad-details">
            {isFacebook && (
              <div className="display-url">
                {websiteURL || "www.yourwebsite.com"}
              </div>
            )}
            <div className={isFacebook ? "headline-text" : "insta-headline"}>
              {headline || "Your Headline"}
            </div>
            <div className="desc-text">
              {feedLinkDescription || "Your Link Description"}
            </div>
            {!isFacebook && (
            <span className="desc-text">
              {primaryText || "Primary Text"}
            </span>
          )}
          </div>
          {isFacebook && (
            <div className="reactions">
              <span>
                <IconLike /> Like
              </span>
              <span>
                <IconComment /> Comment
              </span>
              <span>
                <IconShare /> Share
              </span>
            </div>
          )}
        </div>
        {showSettings && (
          <div className="ads-creative-settings">
            <div className="settings-form">
              <div className="section-header">
                <span>Images</span>
                <Close onClick={() => setShowSettings(false)} />
              </div>
              <div className="form-group">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={imageSource === "catalogue"}
                      onChange={() => {
                        setImageSource("catalogue");
                        setAdsCreativeCards(adsCreativeCards.map((card) =>
                          card?.ad_card_id === id ? { ...card, marketing_image_source: "CATALOGUE" } : card
                        ));
                      }}
                      sx={{
                        paddingBlock: "2px",
                        color: "#BAB7B7",
                        "&.Mui-checked": { color: "#61D5A9" },
                        "& .MuiSvgIcon-root": { height: "12px", width: "12px" },
                      }}
                    />
                  }
                  sx={{
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "10px !important",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      color: "#3d4344",
                    },
                  }}
                  label="Use Catalogue image"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={imageSource === "media"}
                      onChange={() => {
                        setImageSource("media");
                        setAdsCreativeCards(adsCreativeCards.map((card) =>
                          card?.ad_card_id === id ? { ...card, marketing_image_source: "MEDIA_LIBRARY" } : card
                        ));
                      }}
                      sx={{
                        paddingBlock: "2px",
                        color: "#BAB7B7",
                        "&.Mui-checked": { color: "#61D5A9" },
                        "& .MuiSvgIcon-root": { height: "12px", width: "12px" },
                      }}
                    />
                  }
                  sx={{
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "10px !important",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      color: "#3d4344",
                    },
                  }}
                  label="Use images from the Media library"
                />
                {imageSource === "media" && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                      fontSize: "10px",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      color: "#3d4344",
                      marginTop: "4px",
                      paddingLeft: "0px",
                    }}
                    onClick={() => setOpenMediaLibrary(true)}
                  >
                    <AddCircleOutline style={{ fontSize: "14px", color: "gray" }} />
                    Media Library
                  </span>
                )}
              </div>
              <div className="form-group">
                <div className="input-label">LANGUAGE</div>
                <CustomFormControl
                  displayEmpty
                  renderValue={(selected) =>
                    !selected ? (
                      <span style={{ fontSize: "12px", color: "#bab7b7" }}>
                        Select your language
                      </span>
                    ) : (
                      META_TRAVEL_ADS_LANGUAGES.find(
                        (lang) => lang.id_lang === selected,
                      )?.language_name
                    )
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "5px",
                        border: "1px solid #C4C4C4",
                        boxShadow: "none",
                      },
                    },
                  }}
                  value={language}
                  onChange={handleChange.language}
                >
                  {META_TRAVEL_ADS_LANGUAGES.map((option) => (
                    <MenuItem
                      className="dropdown-item"
                      key={option.id_lang}
                      value={option.id_lang}
                    >
                      {option.language_name}
                    </MenuItem>
                  ))}
                </CustomFormControl>
              </div>

              <div className="form-group">
                <div className="input-label">
                  PRIMARY TEXT{" "}
                  <Tooltip
                    slotProps={{ tooltip: { sx: tooltipStyles } }}
                    placement="top"
                    title={
                      <TooltipContent text="The primary text for your ad appears in most placements, though the position varies. You can use @ to tag Facebook profiles or Pages. We recommend 125 characters or less." />
                    }
                  >
                    <InfoOutlined />
                  </Tooltip>
                </div>
                <CustomOutlinedInput
                  placeholder="Enter the primary text"
                  value={primaryText}
                  onChange={handleChange.primaryText}
                  onBlur={handlePrimaryTextBlur}
                  endAdornment={
                    <AddCircleOutline
                      fontSize="8px"
                      style={{ color: "gray", cursor: "pointer" }}
                      onClick={(event) => {
                        setAnchorEl(event.currentTarget);
                      }}
                    />
                  }
                  helperText={
                    primaryTextStatus
                      ? primaryTextStatus.error || "Primary Text"
                      : "Primary Text"
                  }
                  error={primaryTextStatus && !primaryTextStatus.success}
                  success={primaryTextStatus && primaryTextStatus.success}
                />
                <Menu
                  anchorEl={anchorEl}
                  open={openPrimarytextMenu}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  {primaryTextDropdownOptions
                    .filter((option) => !primaryText.includes(`{{${option.id}}}`))
                    .map((option) => (
                    <MenuItem
                      className="dropdown-item"
                      key={option.id}
                      value={option.id}
                      onClick={() => {
                        handleChange.primaryText({
                          target: { value: `${primaryText} {{${option.id}}}` },
                        });
                      }}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </Menu>
              </div>

              <div className="form-group">
                <div className="input-label">WEBSITE URL</div>
                <CustomOutlinedInput
                  placeholder="https://www.example.com"
                  value={websiteURL}
                  onChange={handleChange.websiteURL}
                  onBlur={handleWebsiteURLBlur}
                  helperText={
                    websiteURLStatus
                      ? websiteURLStatus.error || "Website URL"
                      : "Website URL"
                  }
                  error={websiteURLStatus && !websiteURLStatus.success}
                  success={websiteURLStatus && websiteURLStatus.success}
                />
              </div>

              <div className="form-group">
                <div className="input-label">HEADLINE</div>
                <CustomOutlinedInput
                  placeholder="Enter the headline"
                  value={headline}
                  onChange={handleChange.headline}
                  onBlur={handleHeadlineBlur}
                  endAdornment={
                    <AddCircleOutline
                      fontSize="8px"
                      style={{ color: "gray", cursor: "pointer" }}
                      onClick={(event) => {
                        setHeadlineAnchorEl(event.currentTarget);
                      }}
                    />
                  }
                  FormHelperTextProps={{
                    className: "custom-helper-text",
                  }}
                  helperText={
                    headlineStatus
                      ? headlineStatus.error || "Headline"
                      : "Headline"
                  }
                  error={headlineStatus && !headlineStatus.success}
                  success={headlineStatus && headlineStatus.success}
                />
                <Menu
                  anchorEl={headlineAnchorEl}
                  open={openHeadlineMenu}
                  onClose={() => setHeadlineAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  {primaryTextDropdownOptions
                    .filter((option) => !headline.includes(`{{${option.id}}}`))
                    .map((option) => (
                    <MenuItem
                      className="dropdown-item"
                      key={option.id}
                      value={option.id}
                      onClick={() => {
                        handleChange.headline({
                          target: { value: `${headline} {{${option.id}}}` },
                        });
                      }}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </Menu>
              </div>

              <div className="form-group">
                <div className="input-label">
                  DESCRIPTION{" "}
                  <Tooltip
                    slotProps={{ tooltip: { sx: tooltipStyles } }}
                    placement="top"
                    title={
                      <TooltipContent text="The description is additional text that appears in some placements. Its position in the ad varies by placement." />
                    }
                  >
                    <InfoOutlined />
                  </Tooltip>
                </div>
                <CustomOutlinedInput
                  placeholder="Enter the description"
                  value={feedLinkDescription}
                  onChange={handleChange.feedLinkDescription}
                  onBlur={handleFeedLinkDescriptionBlur}
                  endAdornment={
                    <AddCircleOutline
                      fontSize="8px"
                      style={{ color: "gray", cursor: "pointer" }}
                      onClick={(event) => {
                        setDescriptionAnchorEl(event.currentTarget);
                      }}
                    />
                  }
                  helperText={
                    feedLinkDescriptionStatus
                      ? feedLinkDescriptionStatus.error ||
                        "Description"
                      : "Description"
                  }
                  error={
                    feedLinkDescriptionStatus &&
                    !feedLinkDescriptionStatus.success
                  }
                  success={
                    feedLinkDescriptionStatus &&
                    feedLinkDescriptionStatus.success
                  }
                />
                <Menu
                  anchorEl={descriptionAnchorEl}
                  open={openDescriptionMenu}
                  onClose={() => setDescriptionAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                >
                  {primaryTextDropdownOptions
                    .filter((option) => !feedLinkDescription.includes(`{{${option.id}}}`))
                    .map((option) => (
                    <MenuItem
                      className="dropdown-item"
                      key={option.id}
                      value={option.id}
                      onClick={() => {
                        handleChange.feedLinkDescription({
                          target: { value: `${feedLinkDescription} {{${option.id}}}` },
                        });
                      }}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </Menu>
              </div>

              <div className="form-group">
                <div className="input-label">
                  CALL TO ACTION{" "}
                  <Tooltip
                    slotProps={{ tooltip: { sx: tooltipStyles } }}
                    title={
                      <TooltipContent text="Show a button or link on your ad that represents the action you want people to take." />
                    }
                    placement="top"
                  >
                    <InfoOutlined />
                  </Tooltip>
                </div>
                <CustomFormControl
                  displayEmpty
                  helperTextFontSize="10px"
                  renderValue={(selected) =>
                    !selected ? (
                      <span style={{ fontSize: "12px", color: "#bab7b7" }}>
                        Select the call to action
                      </span>
                    ) : (
                      callToActionOptions.find(
                        (option) => option.id === selected,
                      )?.name
                    )
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "5px",
                        border: "1px solid #C4C4C4",
                        boxShadow: "none",
                      },
                    },
                  }}
                  value={callToAction}
                  onChange={handleChange.callToAction}
                  onBlur={handleCallToActionBlur}
                  helperText={
                    callToActionStatus
                      ? callToActionStatus.error || "Call to Action"
                      : "Call to Action"
                  }
                  error={callToActionStatus && !callToActionStatus.success}
                  success={callToActionStatus && callToActionStatus.success}
                >
                  {callToActionOptions.map((option) => (
                    <MenuItem
                      className="dropdown-item"
                      key={option.id}
                      value={option.id}
                    >
                      {option.name}
                    </MenuItem>
                  ))}
                </CustomFormControl>
              </div>

              {callToAction !== "BOOK_TRAVEL" && (
              <div className="form-group">
                <div className="input-label">CALL TO ACTION URL</div>
                <CustomOutlinedInput
                  placeholder="Enter a call to action URL"
                  value={deepLinkToWebsite}
                  onChange={handleChange.deepLinkToWebsite}
                  onBlur={handleDeepLinkBlur}
                  helperText={
                    deepLinkToWebsiteStatus
                      ? deepLinkToWebsiteStatus.error || "Call to Action URL"
                      : "Call to Action URL"
                  }
                  error={
                    deepLinkToWebsiteStatus && !deepLinkToWebsiteStatus.success
                  }
                  success={
                    deepLinkToWebsiteStatus && deepLinkToWebsiteStatus.success
                  }
                />
              </div>
              )}

              <div className="multi-ads-checkbox">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isMultiAds}
                      onChange={handleChange.multiAds}
                      sx={{
                        paddingBlock: "2px",
                        color: "#BAB7B7",
                        "&.Mui-checked": {
                          color: "#61D5A9",
                        },
                        "& .MuiSvgIcon-root": {
                          height: "16px",
                          width: "16px",
                        },
                      }}
                    />
                  }
                  sx={{
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "10px !important",
                      fontFamily: "Inter",
                      fontWeight: 500,
                      color: "#3d4344",
                    },
                  }}
                  label={"Multi-advertiser Ads"}
                />
                <Tooltip
                  slotProps={{ tooltip: { sx: tooltipStyles } }}
                  placement="top"
                  title={
                    <TooltipContent
                      text="Your ad can appear with others in the same ad unit to help
                  promote discoverability. Your ad creative may be resized or
                  cropped."
                    />
                  }
                >
                  <InfoOutlined />
                </Tooltip>
              </div>
            </div>
          </div>
        )}
      </div>
      <MetaMediaLibrary
        isOpen={openMediaLibrary}
        onClose={() => setOpenMediaLibrary(false)}
        hotelId={hotelId}
        currentMarketingImage={editableCard?.marketing_image?.url || null}
        onSave={(selectedImages) => {
          mediaLibraryImageRef.current = selectedImages.marketing_image;
          setAdsCreativeCards(
            adsCreativeCards.map((card) =>
              card?.ad_card_id === id
                ? {
                    ...card,
                    marketing_image: { url: selectedImages.marketing_image },
                  }
                : card,
            ),
          );
        }}
      />
    </>
  );
};

export default AdsCreativePreviewCard;
